// src/pages/invoices/InvoiceForm.tsx
import SelectDropdown from '@/components/common/SelectDropdown';
import { SimpleTooltip } from '@/components/common/tooltip';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ASSET_BASE_URL } from '@/config';
import { getBankOptionLabel } from '@/lib/constants/bank-details';
import { states } from '@/lib/constants/common';
import { getIndustryLabel } from '@/lib/constants/company';
import {
  getCustomerTypeLabel,
  getGstTreatmentLabel,
  getGstTypeLabel,
} from '@/lib/constants/customors';
import {
  invoiceStatusOptions,
  invoiceTermsOptions,
  invoiceTitleOptions,
} from '@/lib/constants/invoice';
import { invoiceSchema } from '@/lib/schemas/invoice.schema';
import { bankDetailsAPI, companiesAPI, customersAPI, invoicesAPI, itemsAPI } from '@/lib/utils/api';
import { formatDateToYYYYMMDD } from '@/lib/utils/date';
import {
  calculateDueDate,
  getGstType,
  getTermsDescription,
  numberToWords,
} from '@/lib/utils/invoice';
import { BankDetails } from '@/types/bank-details.types';
import { Company } from '@/types/company.types';
import { Customer } from '@/types/customer.types';
import { InvoiceFormData, InvoiceItem } from '@/types/invoice.types';
import { Item } from '@/types/items.types';
import { Form, Formik, FormikHelpers } from 'formik';
import { Switch } from '@/components/ui/switch';
import {
  ArrowLeft,
  Building2,
  CreditCard,
  Globe,
  Info,
  Mail,
  MapPin,
  Phone,
  User,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import BankDropdown from './components/BankDropdown';
import CompanyDropdown from './components/CompanyDropdown';
import CustomerDropdown from './components/CustomerDropdown';
import InvoiceItemsTable from './components/InvoiceItemsTable';
import InvoiceSummary from './components/InvoiceSummary';
import ItemModal from './components/ItemModal';

const createEmptyItem = (): InvoiceItem => ({
  id: Date.now(),
  itemId: '',
  itemName: '',
  description: '',
  quantity: 1,
  rate: 0,
  amount: 0,
  hsnSac: '',
  taxPreference: '',
  taxRate: 0,
  taxValue: '',
  sellingPrice: 0,
  itemType: 'service',
});

const getNormalizedInvoiceTitleType = (invoiceData: any): string => {
  const rawTitleType =
    invoiceData?.invoiceTitleType ??
    invoiceData?.InvoiceTitleType ??
    invoiceData?.invoiceTitle ??
    invoiceData?.invoiceTitleLabel ??
    'tax_invoice';
  const normalized = String(rawTitleType).trim().toLowerCase();

  if (normalized.includes('proforma')) return 'proforma_invoice';
  if (normalized.includes('overdue')) return 'overdue_invoice';
  if (normalized.includes('consolidated')) return 'consolidated_invoice';
  if (normalized.includes('retainer')) return 'retainer_invoice';
  if (normalized.includes('interim')) return 'interim_invoice';
  if (normalized.includes('timesheet')) return 'timesheet_invoice';
  if (normalized.includes('final')) return 'final_invoice';
  return 'tax_invoice';
};

const InvoiceForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();

  // const timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  // console.log(timeZoneName);
  // Example output: "Asia/Calcutta" (or the user's actual time zone)

  const today = formatDateToYYYYMMDD(new Date());

  const tenDaysLater = new Date();
  tenDaysLater.setDate(tenDaysLater.getDate() + 10);
  const tenDaysLaterStr = formatDateToYYYYMMDD(tenDaysLater);

  const state = location.state as { invoiceData?: any; isEdit?: boolean } | null;
  const isEdit = Boolean(state?.isEdit && state?.invoiceData && id);
  const invoiceData = state?.invoiceData ?? null;

  // Initial form data
  const initialFormData: InvoiceFormData = {
    _id: invoiceData?._id || '',
    invoiceNumber: invoiceData?.invoiceNumber || '',
    invoiceType: invoiceData?.invoiceType || '',
    companyId: invoiceData?.companyId?._id || '',
    customerId: invoiceData?.customerId?._id || '',
    invoiceDate: invoiceData?.invoiceDate
      ? formatDateToYYYYMMDD(new Date(invoiceData.invoiceDate))
      : today,
    dueDate: invoiceData?.dueDate
      ? formatDateToYYYYMMDD(new Date(invoiceData.dueDate))
      : tenDaysLaterStr,
    placeOfSupply: invoiceData?.placeOfSupply || '',
    terms: invoiceData?.terms || 'Net 10',
    invoiceTitleType: getNormalizedInvoiceTitleType(invoiceData),
    currency: invoiceData?.currency || 'INR',
    status: invoiceData?.status || 'sent',
    paymentSource: invoiceData?.paymentSource || '',
    subTotal: invoiceData?.subTotal || 0,
    taxTotal: invoiceData?.taxTotal || 0,
    totalAmount: invoiceData?.totalAmount || 0,
    amountInWords: invoiceData?.amountInWords || '',
    individualName: invoiceData?.individualName || '',
    selectedBankId: invoiceData?.selectedBankId?._id || '',
    items: invoiceData?.items?.map((item: any, index: number) => ({
      id: Date.now() + index,
      rate: item.rate || 0,
      taxRate: item.taxRate || 0,
      quantity: item.quantity || 1,
      amount: item.amount || 0,
      itemId: item.itemId?._id || item.itemId || '',
      itemName: item.itemId?.itemName || item.itemName || '',
      itemType: item.itemId?.itemType || item.itemType || 'service',
      description: item.itemId?.description || item.description || '',
      hsnSac: item.itemId?.hsnSac || item.hsnSac || '',
      taxPreference: item.itemId?.taxPreference || item.taxPreference || '',
      taxValue: item.itemId?.taxValue || item.taxValue || String(item.taxRate || ''),
      sellingPrice: item.itemId?.sellingPrice || item.sellingPrice || item.rate || 0,
      currency: item.itemId?.currency || item.currency || 'INR',
    })) || [createEmptyItem()],
    isDraft: invoiceData?.isDraft || false,
    taxBreakdown: invoiceData?.taxBreakdown || {},
    branding:
      invoiceData?.invoiceNumber &&
      localStorage.getItem(`branding_${invoiceData.invoiceNumber}`) !== null
        ? localStorage.getItem(`branding_${invoiceData.invoiceNumber}`) === 'true'
        : (invoiceData?.branding ?? true),
  };

  // State
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [availableItems, setAvailableItems] = useState<Item[]>([]);
  const [bankDetails, setBankDetails] = useState<BankDetails[]>([]);

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showItemModal, setShowItemModal] = useState(false);
  const [activeItemRowId, setActiveItemRowId] = useState<number | null>(null);
  const pendingNewItemRef = useRef<Item | null>(null);
  const formikRef = useRef<any>(null);

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    try {
      const [c1, c2, c3, c4] = await Promise.all([
        companiesAPI.getAll(),
        customersAPI.getAll(),
        itemsAPI.getAll(),
        bankDetailsAPI.getAll(),
      ]);

      setCompanies(c1.data.companies || []);
      setCustomers(c2.data.customers || []);
      setAvailableItems(c3.data.items || []);
      setBankDetails(c4.data.bankDetails || []);
    } catch {
      toast.error('Failed to load form data');
    }
  };

  useEffect(() => {
    fetchDropdownData().finally(() => setLoadingData(false));
  }, []);

  // Calculate totals
  const calculateTotals = (items: InvoiceItem[], currency: string) => {
    const subtotal = items.reduce((sum, item) => sum + item.rate * item.quantity, 0);

    const taxGroups: Record<string, number> = {};
    items.forEach((item) => {
      const base = item.rate * item.quantity;
      const slab = item.taxRate || 0;
      if (slab > 0) {
        if (!taxGroups[slab]) taxGroups[slab] = 0;
        taxGroups[slab] += (base * slab) / 100;
      }
    });

    const totalTax = Object.values(taxGroups).reduce((s, v) => s + v, 0);
    const totalAmount = subtotal + totalTax;
    const amountInWords = numberToWords(totalAmount, currency);

    return {
      subTotal: subtotal,
      taxTotal: totalTax,
      totalAmount,
      amountInWords,
      taxBreakdown: taxGroups,
    };
  };

  // Handle submit
  const handleSubmit = async (
    values: InvoiceFormData,
    { setSubmitting }: FormikHelpers<InvoiceFormData>
  ) => {
    setLoading(true);

    try {
      const totals = calculateTotals(values.items, values.currency);

      const submitData: any = {
        invoiceNumber: values.invoiceNumber,
        invoiceType: values.companyId ? 'company' : 'individual',
        customerId: values.customerId,
        placeOfSupply: values.placeOfSupply,
        invoiceDate: values.invoiceDate,
        dueDate: values.dueDate,
        terms: values.terms?.trim() || 'due',
        invoiceTitleType: values.invoiceTitleType || 'tax_invoice',
        currency: values.currency,
        status: values.status || 'sent',
        paymentSource: values.paymentSource,
        subTotal: totals.subTotal,
        totalAmount: totals.totalAmount,
        amountInWords: totals.amountInWords,
        branding: values.branding,
        isDraft: false,
        items: values.items.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity,
          rate: item.rate,
          taxRate: item.taxRate,
          taxValue: item.taxValue,
          amount: item.amount,
        })),
      };

      if (values.companyId?.trim()) {
        submitData.companyId = values.companyId;
        submitData.individualName = '';
      } else {
        submitData.companyId = null;
        submitData.individualName = values.individualName?.trim() || '';
      }

      if (values.selectedBankId?.trim()) {
        submitData.selectedBankId = values.selectedBankId;
      }

      if (isEdit) {
        await invoicesAPI.update(id as string, submitData);
        toast.success('Invoice updated successfully');
      } else {
        await invoicesAPI.create(submitData);
        toast.success('Invoice created successfully');
      }

      // Pure frontend local storage save for branding
      localStorage.setItem(`branding_${values.invoiceNumber}`, values.branding ? 'true' : 'false');

      navigate('/invoices', { state: { refresh: true } });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save invoice');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  // Handle save draft
  const handleSaveDraft = async (values: InvoiceFormData) => {
    setLoading(true);

    try {
      const validItems = values.items
        .filter((it) => it.itemId && typeof it.itemId === 'string' && it.itemId.length === 24)
        .map((it) => ({
          itemId: it.itemId,
          quantity: it.quantity || 1,
          rate: it.rate || 0,
          taxRate: it.taxRate || 0,
          taxValue: it.taxValue || '',
          amount: it.amount || 0,
        }));

      const draftData: any = {
        invoiceNumber: values.invoiceNumber || `DRAFT-${Date.now()}`,
        isDraft: true,
        status: values.status || 'sent',
        customerId: values.customerId || undefined,
        invoiceType: values.companyId ? 'company' : 'individual',
        invoiceDate: values.invoiceDate || formatDateToYYYYMMDD(new Date()),
        dueDate: values.dueDate || '',
        terms: values.terms?.trim() || 'due',
        invoiceTitleType: values.invoiceTitleType || 'tax_invoice',
        currency: values.currency || 'INR',
        branding: values.branding ?? true,
        items: validItems,
        placeOfSupply: values.placeOfSupply || undefined,
        paymentSource: values.paymentSource || undefined,
      };

      if (values.companyId?.trim()) {
        draftData.companyId = values.companyId;
        draftData.individualName = '';
      } else {
        draftData.companyId = null;
        draftData.individualName = values.individualName?.trim() || '';
      }

      if (values.selectedBankId?.trim()) {
        draftData.selectedBankId = values.selectedBankId;
      }

      if (isEdit) {
        await invoicesAPI.update(id as string, draftData);
        toast.success('Draft updated successfully');
      } else {
        await invoicesAPI.create(draftData);
        toast.success('Invoice saved as draft successfully');
      }

      // Pure frontend local storage save for branding
      localStorage.setItem(`branding_${values.invoiceNumber}`, values.branding ? 'true' : 'false');

      navigate('/invoices', { state: { refresh: true } });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  // Handle item added from modal
  const handleItemAdded = useCallback(
    async (newItem: Item) => {
      // Store the new item for auto-selection
      pendingNewItemRef.current = newItem;

      setAvailableItems((prev) => [...prev, newItem]);

      try {
        const itemsRes = await itemsAPI.getAll();
        setAvailableItems(itemsRes.data.items || []);
      } catch (err) {
        console.error('Failed to refresh item list:', err);
      }

      setShowItemModal(false);

      // Auto-select the newly created item in the active row
      if (activeItemRowId !== null && formikRef.current) {
        const { values, setFieldValue } = formikRef.current;
        const updatedItems = values.items.map((item: InvoiceItem) => {
          if (item.id !== activeItemRowId) return item;

          const next = { ...item };
          next.itemId = newItem._id;
          next.itemName = newItem.itemName;
          next.description = newItem.description || '';
          next.rate = Number(newItem.sellingPrice || 0);
          next.hsnSac = newItem.itemType === 'service' ? newItem.sacCode : newItem.hsnCode;
          next.taxPreference = newItem.taxPreference || '';
          next.sellingPrice = newItem.sellingPrice || 0;
          next.itemType = newItem.itemType || 'goods';
          next.quantity = Number(next.quantity || 1);

          // Calculate amounts
          const baseAmount = next.rate * next.quantity;
          const taxAmount = (baseAmount * (next.taxRate || 0)) / 100;
          next.taxAmount = Number(taxAmount.toFixed(2));
          next.amount = Number((baseAmount + taxAmount).toFixed(2));
          next.baseAmount = Number(baseAmount.toFixed(2));

          return next;
        });

        setFieldValue('items', updatedItems);
        setActiveItemRowId(null);
        pendingNewItemRef.current = null;
      }
    },
    [activeItemRowId]
  );

  if (loadingData) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pt-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          onClick={() => navigate('/invoices')}
          className="text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </Button>
        <h1 className="text-2xl font-bold">{isEdit ? 'Edit Invoice' : 'Create Invoice'}</h1>
      </div>

      {/* Form */}
      <Card>
        <CardContent>
          <Formik
            initialValues={initialFormData}
            validationSchema={invoiceSchema}
            onSubmit={handleSubmit}
          >
            {(formikProps) => {
              const { values, errors, touched, handleChange, setFieldValue, setFieldTouched } =
                formikProps;
              // Keep formikRef in sync so handleItemAdded can access it
              formikRef.current = formikProps;
              const currentCompany = values.companyId
                ? (companies.find((c) => c._id === values.companyId) ?? null)
                : null;

              const currentCustomer = values.customerId
                ? (customers.find((c) => c._id === values.customerId) ?? null)
                : null;

              const isCustomerTaxable = Boolean(
                currentCustomer?.gstTreatment?.taxPreference === 'taxable' && currentCompany
              );

              const isCompanyAddressAvailable = Boolean(
                currentCompany?.companyAddress?.addressLine1 ||
                currentCompany?.companyAddress?.addressLine2 ||
                currentCompany?.companyAddress?.city ||
                currentCompany?.companyAddress?.state ||
                currentCompany?.companyAddress?.pinCode
              );

              const isCustomerAddressAvailable = Boolean(
                currentCustomer?.customerAddress?.address ||
                currentCustomer?.customerAddress?.city ||
                currentCustomer?.customerAddress?.state ||
                currentCustomer?.customerAddress?.pinCode
              );

              const companyState = currentCompany?.placeOfSupply;
              const customerState = values.placeOfSupply;
              // const customerState = currentCustomer?.customerAddress?.state;

              const taxType = getGstType(companyState, customerState);

              // Helper to get array-level error
              const getItemsError = (): string | undefined => {
                if (touched.items && typeof errors.items === 'string') {
                  return errors.items;
                }
                return undefined;
              };

              // Helper to get item-level errors
              const getItemErrors = () => {
                if (Array.isArray(errors.items)) {
                  return errors.items;
                }
                return undefined;
              };

              // Helper to get items touched state
              const getItemsTouched = () => {
                if (Array.isArray(touched.items)) {
                  return touched.items;
                }
                return touched.items;
              };

              const handleRemoveCompany = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                setFieldValue('companyId', '');
              };

              const handleRemoveBank = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.preventDefault();
                e.stopPropagation();
                setFieldValue('selectedBankId', ''); // Clear the bank selection
              };

              const handleTermsChange = (newTerms: string) => {
                setFieldValue('terms', newTerms);

                // Calculate and set new due date based on invoice date and new terms
                const newDueDate = calculateDueDate(values.invoiceDate, newTerms);

                // Only update due date if calculation returns a value
                // (Custom returns null, so user can set it manually)
                if (newDueDate) {
                  setFieldValue('dueDate', newDueDate);
                }
              };

              const handleInvoiceDateChange = (date: Date | undefined) => {
                if (!date) {
                  setFieldValue('invoiceDate', '');
                  return;
                }

                // Convert Date to string format (YYYY-MM-DD)
                const newDate = formatDateToYYYYMMDD(date);
                setFieldValue('invoiceDate', newDate);

                // Recalculate due date based on new invoice date and current terms
                // Skip if terms is 'Custom' (user controls due date manually)
                if (values.terms !== 'Custom') {
                  const newDueDate = calculateDueDate(newDate, values.terms);
                  if (newDueDate) {
                    setFieldValue('dueDate', newDueDate);
                  }
                }
              };

              const handleDueDateChange = (date: Date | undefined) => {
                if (!date) {
                  setFieldValue('dueDate', '');
                  return;
                }

                const newDueDate = formatDateToYYYYMMDD(date);
                setFieldValue('dueDate', newDueDate);

                // Check if the new due date matches the calculated date for current terms
                // If not, switch to "Custom" terms
                if (values.terms !== 'Custom' && values.invoiceDate) {
                  const calculatedDueDate = calculateDueDate(values.invoiceDate, values.terms);

                  if (calculatedDueDate && newDueDate !== calculatedDueDate) {
                    setFieldValue('terms', 'Custom');
                  }
                }
              };

              return (
                <Form className="space-y-6">
                  {/* Basic Information */}
                  <div>
                    <h3 className="text-lg font-medium mb-4">Basic Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Invoice Number */}
                      <div className="space-y-2">
                        <Label>Invoice Number *</Label>
                        <div className="space-y-1">
                          <Input
                            name="invoiceNumber"
                            value={values.invoiceNumber}
                            onChange={handleChange}
                            placeholder="e.g., INV-001"
                          />
                          {touched.invoiceNumber && errors.invoiceNumber && (
                            <p className="text-sm text-red-500">{errors.invoiceNumber}</p>
                          )}
                        </div>
                      </div>

                      {/* Status */}
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <SelectDropdown
                          value={values.status}
                          options={invoiceStatusOptions}
                          onChange={(val) => setFieldValue('status', val)}
                        />
                      </div>

                      {/* Invoice Date */}
                      <div className="space-y-2">
                        <Label>Invoice Date *</Label>
                        <div className="space-y-1">
                          <DatePicker
                            format="dd/MM/yyyy"
                            value={values.invoiceDate}
                            onChange={handleInvoiceDateChange}
                            placeholder="Select invoice date"
                            error={!!(touched.invoiceDate && errors.invoiceDate)}
                          />
                          {touched.invoiceDate && errors.invoiceDate && (
                            <p className="text-sm text-red-500">{errors.invoiceDate}</p>
                          )}
                        </div>
                      </div>

                      {/* Terms */}
                      <div className="space-y-2">
                        <Label>Terms</Label>
                        <SelectDropdown
                          value={values.terms}
                          options={[...invoiceTermsOptions]}
                          onChange={handleTermsChange}
                        />
                        {values.terms && getTermsDescription(values.terms, values.invoiceDate) && (
                          <p className="text-xs text-muted-foreground">
                            {getTermsDescription(values.terms, values.invoiceDate)}
                          </p>
                        )}
                      </div>

                      {/* Invoice Title */}
                      <div className="space-y-2">
                        <Label>Invoice Title</Label>
                        <SelectDropdown
                          value={values.invoiceTitleType || 'tax_invoice'}
                          options={invoiceTitleOptions}
                          onChange={(val) => setFieldValue('invoiceTitleType', val)}
                        />
                      </div>

                      {/* Due Date */}
                      <div className="space-y-2">
                        <Label>Due Date *</Label>
                        <div className="space-y-1">
                          <DatePicker
                            format="dd/MM/yyyy"
                            value={values.dueDate}
                            onChange={handleDueDateChange}
                            placeholder="Select due date"
                            error={!!(touched.dueDate && errors.dueDate)}
                          />
                          {touched.dueDate && errors.dueDate && (
                            <p className="text-sm text-red-500">{errors.dueDate}</p>
                          )}
                        </div>
                      </div>

                      {/* Payment Source */}
                      <div className="space-y-2">
                        <Label>Payment Source</Label>
                        <Input
                          name="paymentSource"
                          value={values.paymentSource}
                          onChange={handleChange}
                          placeholder="e.g., Bank Transfer, Cash"
                        />
                      </div>

                      {/* Place of Supply */}
                      <div className="space-y-2">
                        <Label>Place of Supply *</Label>
                        <div className="space-y-1">
                          <SelectDropdown
                            value={values.placeOfSupply}
                            options={[...states]}
                            onChange={(val) => setFieldValue('placeOfSupply', val)}
                          />
                          {touched.placeOfSupply && errors.placeOfSupply && (
                            <p className="text-sm text-red-500">{errors.placeOfSupply}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Company Section */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium mb-4">Company Details</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Company</Label>
                        <CompanyDropdown
                          companies={companies}
                          value={values.companyId}
                          onChange={(val) => setFieldValue('companyId', val)}
                        />
                      </div>

                      {!values.companyId && (
                        <div className="space-y-2">
                          <Label>Individual Name *</Label>
                          <div className="space-y-1">
                            <Input
                              name="individualName"
                              value={values.individualName}
                              onChange={handleChange}
                              placeholder="Enter individual name (Max 30 chars)"
                              maxLength={20}
                            />
                            {touched.individualName && errors.individualName && (
                              <p className="text-sm text-red-500">{errors.individualName}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Company Preview */}
                      {currentCompany && (
                        <Card className="border-border bg-card">
                          <CardContent>
                            <div className="space-y-4">
                              {/* Header */}
                              <div className="flex items-start gap-4 pb-3 border-b">
                                {currentCompany.companyLogo ? (
                                  <div className="h-14 w-14 rounded-lg border bg-background overflow-hidden shrink-0">
                                    <img
                                      src={`${ASSET_BASE_URL}${currentCompany.companyLogo}`}
                                      alt="Company Logo"
                                      className="h-full w-full object-contain p-1"
                                    />
                                  </div>
                                ) : (
                                  <div className="h-14 w-14 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                    <Building2 className="h-7 w-7 text-primary" />
                                  </div>
                                )}
                                <div className="flex items-center flex-1">
                                  <div className="min-w-0 flex-1">
                                    <h4 className="font-semibold text-base truncate">
                                      {currentCompany.companyName}
                                    </h4>
                                    {currentCompany.companyIndustry && (
                                      <p className="text-sm text-muted-foreground mt-0.5">
                                        {getIndustryLabel(currentCompany.companyIndustry)}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <SimpleTooltip content="Remove company from invoice" side="left">
                                  <button
                                    className="h-9 w-9 flex items-center justify-center bg-destructive/5 hover:bg-destructive/10 rounded-full transition-colors shrink-0"
                                    onClick={handleRemoveCompany}
                                  >
                                    <X className="h-[18px] w-[18px] text-destructive shrink-0" />
                                  </button>
                                </SimpleTooltip>
                              </div>

                              {/* Company Details */}
                              <div className="grid gap-3">
                                {isCompanyAddressAvailable && (
                                  <div className="flex items-center gap-3">
                                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">Address</p>
                                      <p className="text-sm leading-relaxed">
                                        {[
                                          currentCompany.companyAddress?.addressLine1,
                                          currentCompany.companyAddress?.addressLine2,
                                          currentCompany.companyAddress?.city,
                                          currentCompany.companyAddress?.state,
                                          currentCompany.companyAddress?.pinCode,
                                        ]
                                          .filter(Boolean)
                                          .join(', ')}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {currentCompany.companyAddress?.phone && (
                                  <div className="flex items-center gap-3">
                                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">Phone</p>
                                      <p className="text-sm">
                                        {currentCompany.companyAddress.phone}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {currentCompany.companyWebsite && (
                                  <div className="flex items-center gap-3">
                                    <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-xs text-muted-foreground">Website</p>
                                      <a
                                        href={
                                          currentCompany.companyWebsite.startsWith('http')
                                            ? currentCompany.companyWebsite
                                            : `https://${currentCompany.companyWebsite}`
                                        }
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm text-primary hover:underline break-all"
                                      >
                                        {currentCompany.companyWebsite}
                                      </a>
                                    </div>
                                  </div>
                                )}
                                {currentCompany.gstTreatment?.gstType && (
                                  <div className="flex items-start gap-3">
                                    <CreditCard className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">GST Treatment</p>
                                      <p className="text-sm">
                                        {getGstTypeLabel(currentCompany.gstTreatment.gstType)}
                                        {currentCompany.gstTreatment.taxPreference &&
                                          ` • ${getGstTreatmentLabel(currentCompany.gstTreatment.taxPreference)}`}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {currentCompany.baseCurrency && (
                                  <div className="flex items-center gap-3">
                                    <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">Base Currency</p>
                                      <p className="text-sm font-medium">
                                        {currentCompany.baseCurrency}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {currentCompany.placeOfSupply && (
                                  <div className="flex items-center gap-3">
                                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">
                                        Place of Supply
                                      </p>
                                      <p className="text-sm">
                                        {states.find(
                                          (s) => s.value === currentCompany.placeOfSupply
                                        )?.label || currentCompany.placeOfSupply}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>

                  {/* Customer Section */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium mb-4">Customer Details</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Customer *</Label>
                        <CustomerDropdown
                          customers={customers}
                          value={values.customerId}
                          onChange={(val) => {
                            setFieldValue('customerId', val);
                            const customer = customers.find((c) => c._id === val);
                            if (customer?.currency) {
                              setFieldValue('currency', customer.currency);
                            }
                          }}
                          error={
                            touched.customerId && errors.customerId ? errors.customerId : undefined
                          }
                        />
                      </div>

                      {/* Customer Preview Card */}
                      {currentCustomer && (
                        <Card className="border-border bg-card">
                          <CardContent>
                            <div className="space-y-4">
                              {/* Header */}
                              <div className="flex items-start gap-3 pb-3 border-b">
                                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                  <User className="h-5 w-5 text-primary" />
                                </div>
                                <div className="self-center">
                                  <h4 className="font-semibold text-base">
                                    {currentCustomer.primaryContact}
                                  </h4>
                                  {currentCustomer.customerType === 'business' ? (
                                    currentCustomer.companyName && (
                                      <p className="text-sm text-muted-foreground">
                                        {currentCustomer.companyName}
                                      </p>
                                    )
                                  ) : (
                                    <p className="text-sm text-muted-foreground">
                                      {getCustomerTypeLabel(currentCustomer.customerType)}
                                    </p>
                                  )}
                                </div>
                              </div>

                              {/* Contact Details */}
                              <div className="grid gap-3">
                                {currentCustomer.email && (
                                  <div className="flex items-center gap-3">
                                    <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div className="min-w-0">
                                      <p className="text-xs text-muted-foreground">Email</p>
                                      <p className="text-sm truncate">{currentCustomer.email}</p>
                                    </div>
                                  </div>
                                )}

                                {currentCustomer.phone && (
                                  <div className="flex items-center gap-3">
                                    <Phone className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">Phone</p>
                                      <p className="text-sm">{currentCustomer.phone}</p>
                                    </div>
                                  </div>
                                )}

                                {isCustomerAddressAvailable && (
                                  <div className="flex items-center gap-3">
                                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">Address</p>
                                      <p className="text-sm leading-relaxed">
                                        {[
                                          currentCustomer?.customerAddress?.address,
                                          currentCustomer?.customerAddress?.city,
                                          currentCustomer?.customerAddress?.state,
                                          currentCustomer?.customerAddress?.pinCode,
                                        ]
                                          .filter(Boolean)
                                          .join(', ')}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {currentCustomer.gstTreatment?.gstType && (
                                  <div className="flex items-start gap-3">
                                    <CreditCard className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">GST Treatment</p>
                                      <p className="text-sm">
                                        {getGstTypeLabel(currentCustomer.gstTreatment.gstType)}
                                        {currentCustomer.gstTreatment.taxPreference &&
                                          ` • ${getGstTreatmentLabel(currentCustomer.gstTreatment.taxPreference)}`}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                {currentCustomer.currency && (
                                  <div className="flex items-center gap-3">
                                    <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                                    <div>
                                      <p className="text-xs text-muted-foreground">Currency</p>
                                      <p className="text-sm font-medium">
                                        {currentCustomer.currency}
                                      </p>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>

                  {/* Bank Details Section */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-medium mb-4">Bank Details</h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Bank Account</Label>
                        <BankDropdown
                          bankDetails={bankDetails}
                          value={values.selectedBankId}
                          onChange={(val) => setFieldValue('selectedBankId', val)}
                        />
                      </div>

                      {/* Bank Preview Card */}
                      {values.selectedBankId && (
                        <Card className="border-border bg-card">
                          <CardContent>
                            {bankDetails
                              .filter((b) => b._id === values.selectedBankId)
                              .map((bank, index) => (
                                <div key={bank._id} className="space-y-4">
                                  {/* Header */}
                                  <div className="flex items-start gap-3 pb-3 border-b flex-1">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                      <Building2 className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="flex items-center flex-1">
                                      <div className="flex-1">
                                        <h4 className="font-semibold text-base">
                                          {getBankOptionLabel(bank.bankName)}
                                        </h4>
                                        <p className="text-sm text-muted-foreground">
                                          {bank.accountHolderName}
                                        </p>
                                      </div>
                                    </div>

                                    <SimpleTooltip content="Remove bank from invoice" side="left">
                                      <button
                                        className="h-9 w-9 flex items-center justify-center bg-destructive/5 hover:bg-destructive/10 rounded-full transition-colors shrink-0"
                                        onClick={handleRemoveBank}
                                      >
                                        <X className="h-[18px] w-[18px] text-destructive shrink-0" />
                                      </button>
                                    </SimpleTooltip>
                                  </div>

                                  {/* Bank Details */}
                                  <div className="grid gap-3">
                                    <div className="flex items-center gap-3">
                                      <CreditCard className="h-4 w-4 text-muted-foreground shrink-0" />
                                      <div>
                                        <p className="text-xs text-muted-foreground">
                                          Account Number
                                        </p>
                                        <p className="text-sm font-mono">{bank.accountNumber}</p>
                                      </div>
                                    </div>

                                    <div className="flex items-center gap-3">
                                      <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                      <div>
                                        <p className="text-xs text-muted-foreground">IFSC Code</p>
                                        <p className="text-sm font-mono">{bank.ifscCode}</p>
                                      </div>
                                    </div>

                                    {bank.upiId && (
                                      <div className="flex items-center gap-3">
                                        <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                                        <div>
                                          <p className="text-xs text-muted-foreground">UPI ID</p>
                                          <p className="text-sm font-mono break-all">
                                            {bank.upiId}
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>

                  {/* Invoice Items Section */}
                  <div className="border-t pt-6">
                    <InvoiceItemsTable
                      items={values.items}
                      availableItems={availableItems}
                      currency={values.currency}
                      isCustomerTaxable={isCustomerTaxable}
                      onItemsChange={(items) => {
                        setFieldValue('items', items);
                        // Optionally touch all items when they change
                        setFieldTouched('items', true, false);
                      }}
                      onAddItem={() => setFieldValue('items', [...values.items, createEmptyItem()])}
                      onAddNewItem={(rowId) => {
                        setActiveItemRowId(rowId);
                        setShowItemModal(true);
                      }}
                      error={getItemsError()}
                      itemErrors={getItemErrors()}
                      itemsTouched={getItemsTouched()}
                      taxType={taxType}
                    />
                  </div>

                  {/* Summary Section */}
                  <div className="border-t pt-6">
                    <InvoiceSummary
                      items={values.items}
                      currency={values.currency}
                      isCustomerTaxable={isCustomerTaxable}
                      taxType={taxType}
                    />
                  </div>

                  {/* Info Cards */}
                  <div className="space-y-4">
                    <Card className="border-yellow-200 bg-yellow-50">
                      <CardContent>
                        <div className="flex items-start gap-3">
                          <Info className="h-5 w-5 text-yellow-600 mt-0.5 shrink-0" />
                          <div>
                            <h4 className="text-sm font-medium text-yellow-900 mb-2">
                              Invoice Information
                            </h4>
                            <div className="text-sm text-yellow-800 space-y-1">
                              <p>
                                <strong>Company:</strong> Optional. If not selected, invoice will be
                                individual.
                              </p>
                              <p>
                                <strong>Customer:</strong> Required. Currency auto-sets based on
                                customer.
                              </p>
                              <p>
                                <strong>Items:</strong> Add at least one item. Rates are
                                auto-filled.
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-primary/20 bg-primary/5">
                      <CardContent>
                        <div className="flex items-start gap-3">
                          <Info className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                          <div>
                            <h4 className="text-sm font-medium text-primary/90 mb-2">
                              Quick Actions
                            </h4>
                            <p className="text-sm text-primary/80">
                              Use <strong>"Add New"</strong> options in dropdowns to quickly create
                              companies, customers, or items without losing your form data.
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Branding Toggle */}
                  <div className="flex items-center space-x-2 py-2">
                    <Switch
                      id="branding"
                      checked={values.branding}
                      onCheckedChange={(checked) => setFieldValue('branding', checked)}
                    />
                    <label
                      htmlFor="branding"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Hide branding
                    </label>
                  </div>

                  {/* Submit Buttons */}
                  <div className="flex flex-col xs:flex-row justify-end flex-wrap gap-4">
                    <Button type="button" variant="outline" onClick={() => navigate('/invoices')}>
                      Cancel
                    </Button>

                    {(!isEdit || values.isDraft) && (
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={loading}
                        onClick={() => handleSaveDraft(values)}
                      >
                        {loading ? 'Saving...' : 'Save as Draft'}
                      </Button>
                    )}

                    <Button type="submit" disabled={loading}>
                      {loading ? 'Saving...' : isEdit ? 'Update Invoice' : 'Create Invoice'}
                    </Button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </CardContent>
      </Card>

      {/* Item Modal */}
      <ItemModal
        open={showItemModal}
        onClose={() => setShowItemModal(false)}
        onItemAdded={handleItemAdded}
        invoiceCurrency={initialFormData.currency}
      />
    </div>
  );
};

export default InvoiceForm;
